import { PointTooltipProps } from '@nivo/line'
import dynamic from 'next/dynamic'
import { ChartContainer, Stack, Text } from '~/design_system'
import { textStyles } from '~/theme'
import { SadStates } from '~/components/SadStates'
import { useChartFormat, useFormattedTitle } from './format'
import { DefaultChartSkeleton } from './DefaultChartSkeleton'
import { CustomLinkChartProps, GenericChartProps, ReportKeyChartProps } from './ChartTypes'

// nivo elements have to be dynamically imported because d3 went ESM only and nextjs doesn't like it
const ResponsiveLine = dynamic(async () => import('@nivo/line').then(m => m.ResponsiveLine), { ssr: false })

const valueTypes = Object.freeze({
  CURRENCY: 'currency',
  NUMBER: 'number'
})

const Tooltip = ({
  point: {
    data: { yFormatted }
  }
}: PointTooltipProps) => {
  return (
    <div
      style={{
        background: 'white',
        color: 'inherit',
        fontSize: 'inherit',
        borderRadius: '2px',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.25)',
        padding: '5px 9px'
      }}>
      <Stack spacing={0}>
        <Text {...textStyles.body.md.semi}>{yFormatted}</Text>
      </Stack>
    </div>
  )
}

type BaseLineChartProps = GenericChartProps

interface CustomLinkLineChartProps extends BaseLineChartProps, CustomLinkChartProps {}

interface ReportKeyLineChartProps extends BaseLineChartProps, ReportKeyChartProps {}

export type LineChartProps = CustomLinkLineChartProps | ReportKeyLineChartProps

export const LineChart = ({
  data,
  reportKey,
  chartConfig,
  isLoading = true,
  isFetching = true,
  isError,
  isEmpty
}: LineChartProps) => {
  const {
    format = valueTypes.NUMBER,
    xAxisLabel,
    yAxisLabel,
    title = `${yAxisLabel} Per ${xAxisLabel}`,
    subTitle,
    xAxisFormat = label => label
  } = chartConfig

  const chartData = data && [
    {
      id: 'chartData',
      data: data.map(obj => ({ x: obj.id, y: typeof obj.value === 'string' ? parseFloat(obj.value) : obj.value }))
    }
  ]
  const chartFormat = useChartFormat(format)

  return (
    <ChartContainer
      title={useFormattedTitle(title, format)}
      subTitle={subTitle}
      reportKey={reportKey}
      isLoading={isLoading}
      isFetching={isFetching}
      isError={isError}
      isEmpty={isEmpty}>
      <SadStates states={[{ when: isLoading || isFetching, render: () => <DefaultChartSkeleton /> }]}>
        {chartData && (
          <ResponsiveLine
            data={chartData}
            lineWidth={2}
            pointSize={10}
            enableArea
            areaOpacity={0.1}
            enableGridX={false}
            enableGridY={false}
            yFormat={chartFormat}
            tooltip={Tooltip}
            useMesh
            axisBottom={{
              tickSize: 3,
              tickValues: 6,
              tickPadding: 4,
              tickRotation: 45,
              format: xAxisFormat,
              legend: xAxisLabel,
              legendOffset: 54,
              legendPosition: 'middle'
            }}
            axisLeft={{
              tickSize: 3,
              tickValues: 5,
              format: chartFormat,
              legend: yAxisLabel,
              legendOffset: -60,
              legendPosition: 'middle'
            }}
            margin={{ bottom: 60, left: 72, right: 32, top: 24 }}
            colors="#56b499"
          />
        )}
      </SadStates>
    </ChartContainer>
  )
}
